import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useLocation } from "react-router-dom";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import { clearAdminSession } from "../auth/session";
import "./MasterLayout.css";
import "../components/admin/AdminUI.css";

const AdminLayout = ({ children }) => {
  const [sidebarActive, setSidebarActive] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [admin, setAdmin] = useState(null);
  const location = useLocation();

  const logout = () => {
    clearAdminSession();
    window.location.href = "/admin/login";
  };

  useEffect(() => {
    try {
      setAdmin(JSON.parse(localStorage.getItem("admin") || "{}"));
    } catch {
      setAdmin(null);
    }
  }, [location.pathname]);

  const navClass = (navData) => (navData.isActive ? "active-page" : "");

  return (
    <section className={mobileMenu ? "overlay active" : "overlay "}>
      <aside
        className={
          sidebarActive
            ? "sidebar active "
            : mobileMenu
              ? "sidebar sidebar-open"
              : "sidebar"
        }
      >
        <button
          onClick={() => setMobileMenu(false)}
          type="button"
          className="sidebar-close-btn"
        >
          <Icon icon="radix-icons:cross-2" />
        </button>
        <div>
          <Link to="/admin/dashboard" className="sidebar-logo">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/alphaxcloud.svg`}
              alt="AlphaXCloud"
              className="light-logo"
              width={180}
            />
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/alphaxcloud.svg`}
              alt="AlphaXCloud"
              className="dark-logo"
              width={180}
            />
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/logo-icon.svg`}
              alt="AlphaXCloud"
              className="logo-icon"
              width={36}
              height={36}
            />
          </Link>
        </div>
        <div className="sidebar-menu-area">
          <ul className="sidebar-menu" id="sidebar-menu">
            <li>
              <NavLink to="/admin/dashboard" className={navClass}>
                <Icon icon="solar:home-smile-angle-outline" className="menu-icon" />
                <span>Dashboard</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/users" className={navClass}>
                <Icon icon="mdi:account-group-outline" className="menu-icon" />
                <span>Users</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/plans" className={navClass}>
                <Icon icon="mdi:package-variant-closed" className="menu-icon" />
                <span>Plans</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/orders" className={navClass}>
                <Icon icon="mdi:cart-outline" className="menu-icon" />
                <span>Orders</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/payments" className={navClass}>
                <Icon icon="mdi:credit-card-outline" className="menu-icon" />
                <span>Payment Gateways</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/settings" className={navClass}>
                <Icon icon="mdi:cog-outline" className="menu-icon" />
                <span>Site Settings</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/test-email" className={navClass}>
                <Icon icon="mdi:email-fast-outline" className="menu-icon" />
                <span>Test Email</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/pings" className={navClass}>
                <Icon icon="mdi:message-badge-outline" className="menu-icon" />
                <span>Support Chat</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/pages" className={navClass}>
                <Icon icon="mdi:file-document-outline" className="menu-icon" />
                <span>Pages</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/faqs" className={navClass}>
                <Icon icon="mdi:help-circle-outline" className="menu-icon" />
                <span>FAQs</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/testimonials" className={navClass}>
                <Icon icon="mdi:comment-quote-outline" className="menu-icon" />
                <span>Testimonials</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/profile" className={navClass}>
                <Icon icon="mdi:shield-account-outline" className="menu-icon" />
                <span>Admin Profile</span>
              </NavLink>
            </li>
          </ul>
        </div>
      </aside>

      <main className={sidebarActive ? "dashboard-main active" : "dashboard-main"}>
        <div className="navbar-header">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-4">
                <button
                  type="button"
                  className="sidebar-toggle"
                  onClick={() => setSidebarActive(!sidebarActive)}
                >
                  <Icon icon="heroicons:bars-3-solid" className="icon text-2xl" />
                </button>
                <button
                  onClick={() => setMobileMenu(!mobileMenu)}
                  type="button"
                  className="sidebar-mobile-toggle"
                >
                  <Icon icon="heroicons:bars-3-solid" className="icon" />
                </button>
                <Link to="/" className="nav-home-btn" aria-label="Home">
                  <Icon icon="mdi:web" className="icon text-xl" />
                  <span className="nav-home-tip">Home</span>
                </Link>
                <span
                  className="px-3 py-2 radius-8"
                  style={{
                    background: "linear-gradient(135deg, #00a884, #075e54)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                  }}
                >
                  Admin Panel
                </span>
              </div>
            </div>
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-3">
                <ThemeToggleButton />
                <div className="dropdown">
                  <button
                    className="d-flex justify-content-center align-items-center rounded-circle"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <span className="w-40-px h-40-px bg-danger-focus text-danger-main rounded-circle d-flex justify-content-center align-items-center fw-semibold">
                      {(admin?.email || "A")[0].toUpperCase()}
                    </span>
                  </button>
                  <div className="dropdown-menu to-top dropdown-menu-sm">
                    <div className="py-12 px-16 radius-8 bg-primary-50 mb-16 mx-12 mt-12">
                      <h6 className="text-lg text-primary-light fw-semibold mb-0">
                        {admin?.email || "Admin"}
                      </h6>
                      <span className="text-secondary-light fw-medium text-sm">
                        Role: {admin?.role || "admin"}
                      </span>
                    </div>
                    <ul className="to-top-list">
                      <li>
                        <Link
                          className="dropdown-item text-black px-16 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3"
                          to="/admin/profile"
                        >
                          <Icon icon="solar:user-linear" className="icon text-xl" /> My Profile
                        </Link>
                      </li>
                      <li>
                        <button
                          type="button"
                          className="dropdown-item text-black px-16 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3 w-100"
                          onClick={logout}
                        >
                          <Icon icon="lucide:power" className="icon text-xl" /> Log Out
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-main-body">{children}</div>

        <footer className="d-footer">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <p className="mb-0">© {new Date().getFullYear()} AlphaXCloud Admin</p>
            </div>
          </div>
        </footer>
      </main>
    </section>
  );
};

export default AdminLayout;
